import {Injectable} from "@angular/core";

const R = 6371000; // earth radius in meters

@Injectable({
  providedIn: 'root',
})
export class MapUtilsService {
  constructor() {}

  public toRad(deg: number): number {
    return deg * Math.PI / 180;
  }

  // This function is to drag the map when the marker is on the edge, the map dragging is disabled manually when a marker is being dragged
  public autoPanIfNearEdge(pixel: google.maps.Point, mapRef: google.maps.Map) {
    const PAN_PADDING_PX = 10;
    const PAN_SPEED = 10;
    const mapDiv = mapRef.getDiv();
    const width = mapDiv.offsetWidth;
    const height = mapDiv.offsetHeight;

    let dx = 0;
    let dy = 0;

    if (pixel.x < PAN_PADDING_PX) dx = -PAN_SPEED;
    else if (pixel.x > width - PAN_PADDING_PX) dx = PAN_SPEED;

    if (pixel.y < PAN_PADDING_PX) dy = -PAN_SPEED;
    else if (pixel.y > height - PAN_PADDING_PX) dy = PAN_SPEED;

    if (dx !== 0 || dy !== 0) {
      const projection = mapRef.getProjection();
      if (projection) {
        const center = projection.fromLatLngToPoint(mapRef.getCenter());
        const scale = 2 ** mapRef.getZoom();
        const newCenter = new google.maps.Point(
          center.x + dx / scale,
          center.y + dy / scale
        );
        mapRef.setCenter(projection.fromPointToLatLng(newCenter));
      }
    }
  }

  public containerPixelToLatLng(pixel: google.maps.Point, mapRef: google.maps.Map): google.maps.LatLng | null {
    const projection = mapRef.getProjection();
    const center = mapRef.getCenter();
    const zoom = mapRef.getZoom();

    if (!projection || !center || typeof zoom === 'undefined') {
      return null;
    }

    const scale = 2 ** zoom;
    const mapDiv = mapRef.getDiv();
    const centerPoint = projection.fromLatLngToPoint(center);

    return projection.fromPointToLatLng(new google.maps.Point(
      centerPoint.x + (pixel.x - mapDiv.offsetWidth / 2) / scale,
      centerPoint.y + (pixel.y - mapDiv.offsetHeight / 2) / scale
    ));
  }

  public getClosestPolyline(featureCollection, markerLatLng, mapRef?: google.maps.Map, tolerancePx: number = 24) {
    let closetPolyline = null;
    let minDistance = mapRef ? tolerancePx : 100;
    let projection = null;
    let scale = null;
    let markerPx = null;

    if (mapRef) {
      projection = mapRef.getProjection();
      if (!projection) {
        return null;
      }
      scale = 2 ** mapRef.getZoom();
      const markerPoint = projection.fromLatLngToPoint(markerLatLng);
      markerPx = {x: markerPoint.x * scale, y: markerPoint.y * scale};
    }

    featureCollection.forEach(feature => {
      if (feature.getGeometry().getType() === "LineString") {
        const path = feature.getGeometry().getArray();

        // Check each segment of the polyline
        for (let i = 0; i < path.length - 1; i++) {
          const p1 = path[i];
          const p2 = path[i + 1];
          const distance = mapRef
            ? this.distanceToSegmentInPixels(markerPx, p1, p2, projection, scale)
            : this.distanceToSegment(markerLatLng, p1, p2);
          if (distance < minDistance) {
            minDistance = distance;
            closetPolyline = feature;
          }
        }
      }
    });

    return closetPolyline;
  }

  public distanceToSegmentInPixels(pPx: {x: number, y: number}, v: google.maps.LatLng, w: google.maps.LatLng, projection, scale: number): number {
    const vPoint = projection.fromLatLngToPoint(v);
    const wPoint = projection.fromLatLngToPoint(w);

    const vPx = {x: vPoint.x * scale, y: vPoint.y * scale};
    const wPx = {x: wPoint.x * scale, y: wPoint.y * scale};

    const l2 = (vPx.x - wPx.x) ** 2 + (vPx.y - wPx.y) ** 2;
    if (l2 === 0) {
      return Math.sqrt((pPx.x - vPx.x) ** 2 + (pPx.y - vPx.y) ** 2);
    }

    const t = Math.max(0, Math.min(1, ((pPx.x - vPx.x) * (wPx.x - vPx.x) + (pPx.y - vPx.y) * (wPx.y - vPx.y)) / l2));
    const projX = vPx.x + t * (wPx.x - vPx.x);
    const projY = vPx.y + t * (wPx.y - vPx.y);

    return Math.sqrt((pPx.x - projX) ** 2 + (pPx.y - projY) ** 2);
  }

  public distanceToSegment(p, v, w) {
    // Convert to Cartesian
    const P = this._latLngToCartesian(p);
    const V = this._latLngToCartesian(v);
    const W = this._latLngToCartesian(w);

    // Projection length squared
    const l2 = (V.x - W.x) ** 2 + (V.y - W.y) ** 2 + (V.z - W.z) ** 2;
    if (l2 === 0) return this._haversine(p, v); // v == w case

    // Projection factor t
    const t = Math.max(0, Math.min(1, ((P.x - V.x) * (W.x - V.x) + (P.y - V.y) * (W.y - V.y) + (P.z - V.z) * (W.z - V.z)) / l2));
    const proj = {
      x: V.x + t * (W.x - V.x),
      y: V.y + t * (W.y - V.y),
      z: V.z + t * (W.z - V.z)
    };

    // Distance between P and proj
    return Math.sqrt((P.x - proj.x) ** 2 + (P.y - proj.y) ** 2 + (P.z - proj.z) ** 2);
  }

  public projectPointOntoPolyline(point, polylinePath) {
    let totalDistance = 0;
    let minDistanceToSegment = Infinity;
    let distanceAlongLineAtClosest = 0;

    for (let i = 0; i < polylinePath.length - 1; i++) {
      const segStart = polylinePath[i];
      const segEnd = polylinePath[i + 1];

      // Distance along the path so far
      const segmentLength = google.maps.geometry.spherical.computeDistanceBetween(segStart, segEnd);

      // Distance from point to segment
      const distToSeg = google.maps.geometry.spherical.computeDistanceBetween(point, segStart) +
        google.maps.geometry.spherical.computeDistanceBetween(point, segEnd) -
        segmentLength;

      // If closer to this segment, remember position
      if (distToSeg < minDistanceToSegment) {
        minDistanceToSegment = distToSeg;
        distanceAlongLineAtClosest = totalDistance +
          google.maps.geometry.spherical.computeDistanceBetween(segStart, point);
      }

      totalDistance += segmentLength;
    }

    return distanceAlongLineAtClosest; // position in meters from start of polyline
  }

  public getInsertionIndex(point: google.maps.LatLng, path: google.maps.LatLng[]): number {
    let minIndex: number = -1;
    let minDistance = Infinity;

    // Check each segment of the polyline
    for (let i = 0; i < path.length - 1; i++) {
      const p1 = path[i];
      const p2 = path[i + 1];
      const distance = this.distanceToSegment(point, p1, p2);

      if (distance < minDistance) {
        minDistance = distance;
        minIndex = i;
      }
    }

    return minIndex;
  }

  private _haversine(a, b) {
    const dLat = this.toRad(b.lat() - a.lat());
    const dLng = this.toRad(b.lng() - a.lng());
    const lat1 = this.toRad(a.lat());
    const lat2 = this.toRad(b.lat());

    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  private _latLngToCartesian(latLng) {
    const lat = this.toRad(latLng.lat());
    const lng = this.toRad(latLng.lng());
    return {
      x: R * Math.cos(lat) * Math.cos(lng),
      y: R * Math.cos(lat) * Math.sin(lng),
      z: R * Math.sin(lat)
    };
  }
}
